import React from "react";
import {
  APP_LINKS,
  appOpenLink,
  browserKind,
  destinationFor,
} from "../components/applinks/applinks.mjs";
import { isArabic } from "../components/applinks/browserstate";
import "../components/applinks/applinks.css";

// "Open this in the Cinescape app": /app/food, /app/food-order/{orderRef} and
// /app/booking/{bookingId}, an empty id included. These screens exist only in
// the app. When the app is installed the link opens it directly and this page
// is never seen; it is for everyone else, and for browsers that ignore app
// links. The Open button waits for bannerEnabled (applinks.config.mjs), like
// the banner: until the apps that handle these links are out, only the store
// buttons are offered. The store buttons show on every website, test servers
// included, where they install the live app rather than a test build.
// A short link on the test server shows this page too (shortlink.pages.jsx).
// docs/architecture/app-links-tracking-journeys.md §1.2, §1.4 and §6.

const TEXT = {
  en: {
    heading: "Open this in the Cinescape app",
    food: "Order snacks and drinks for your film in the Cinescape app.",
    food_order: "Follow your food order in the Cinescape app, right up to when it is ready.",
    booking: "Your booking and tickets are in the Cinescape app.",
    short_link: "This link opens in the Cinescape app.",
    signIn_food_order: "Sign in with the account you ordered with.",
    signIn_booking: "Sign in with the account you booked with.",
    open: "Open in the app",
    hint: "If nothing happens, tap ••• and choose Open in browser",
    computer: "Open this link on your phone to go straight to it in the app.",
    getApp: "Don't have the app yet?",
    website: "Continue to the website",
    appStore: "Download on the App Store",
    googlePlay: "Get it on Google Play",
  },
  ar: {
    heading: "افتح هذه الصفحة في تطبيق سينسكيب",
    food: "اطلب الوجبات الخفيفة والمشروبات لفيلمك من تطبيق سينسكيب.",
    food_order: "تابع طلب الطعام الخاص بك في تطبيق سينسكيب حتى يصبح جاهزاً.",
    booking: "حجزك وتذاكرك موجودة في تطبيق سينسكيب.",
    short_link: "هذا الرابط يُفتح في تطبيق سينسكيب.",
    signIn_food_order: "سجّل الدخول بالحساب الذي طلبت به.",
    signIn_booking: "سجّل الدخول بالحساب الذي حجزت به.",
    open: "افتح في التطبيق",
    hint: "إذا لم يحدث شيء، اضغط ••• واختر الفتح في المتصفح",
    computer: "افتح هذا الرابط على هاتفك للانتقال إليه مباشرة في التطبيق.",
    getApp: "ليس لديك التطبيق بعد؟",
    website: "المتابعة إلى الموقع",
    appStore: "حمّله من App Store",
    googlePlay: "احصل عليه من Google Play",
  },
};

// `destination` and `websiteHref` are given by the short-link page; the /app
// pages leave them to the address.
function OpenInApp({ location, config = APP_LINKS, destination: given, websiteHref }) {
  const text = isArabic() ? TEXT.ar : TEXT.en;
  const destination = given || destinationFor(location.pathname) || "food";
  const offersApp = config.bannerEnabled === true;
  const link = offersApp ? appOpenLink(window.location, window.navigator, config) : null;
  const signIn = text["signIn_" + destination];

  const appStore = (
    <a key="ios" href={config.appStoreUrl} target="_blank" rel="noopener noreferrer">
      <img src={new URL("../assets/appstore.png", import.meta.url).href} alt={text.appStore} />
    </a>
  );
  const googlePlay = (
    <a key="android" href={config.playStoreUrl} target="_blank" rel="noopener noreferrer">
      <img src={new URL("../assets/googleplay.png", import.meta.url).href} alt={text.googlePlay} />
    </a>
  );
  const stores = browserKind(window.navigator) === "android" ? [googlePlay, appStore] : [appStore, googlePlay];
  const website = websiteHref || (destination === "booking" ? "/myaccount" : "/");

  return (
    <div className="applinks-page">
      <div className="applinks-page-card">
        <a href="/" className="applinks-page-logo">
          <img src={new URL("../assets/logo.png", import.meta.url).href} alt="Cinescape" />
        </a>
        <h1>{text.heading}</h1>
        <p>{text[destination] || text.food}</p>
        {signIn ? <p className="applinks-page-muted">{signIn}</p> : null}

        {link ? (
          <>
            <a className="applinks-page-open" href={link.href}>
              {text.open}
            </a>
            {link.hint ? <p className="applinks-page-muted">{text.hint}</p> : null}
          </>
        ) : offersApp ? (
          <p className="applinks-page-muted">{text.computer}</p>
        ) : null}

        <p className="applinks-page-stores-title">{text.getApp}</p>
        <div className="applinks-page-stores">{stores}</div>

        <a className="applinks-page-website" href={website}>
          {text.website}
        </a>
      </div>
    </div>
  );
}

export default OpenInApp;
